import { useCallback, useRef } from "react";
import { useDateTimeFieldsContext } from "../context/DateTimeFieldsContext";
import {
  getDateMomentObjectCustom,
  getISOString,
  formatDate,
} from "Utils/DateTimeHelpers";
import { DATE_TIME_CONSTANTS } from "Constants/dateTimeConstants";

/**
 * Custom hook for managing individual date/time field with Context API.
 * This hook connects a specific field to the DateTimeFieldsContext.
 *
 * @param {string} fieldName - Name of the field ('openDate', 'dueDate', 'closeDate')
 * @param {Function} onValidationChange - Optional callback when validation state changes
 * @returns {Object} Hook state and handlers
 */
const useDateTimeField = (fieldName, onValidationChange) => {
  const {
    dates,
    validationErrors,
    updateDate,
    updateTime,
    setValidationError,
    clearValidationError,
    onChange,
  } = useDateTimeFieldsContext();

  // Track if we're in the middle of an update to prevent loops
  const isUpdatingRef = useRef(false);

  /**
   * Handles date change from DatePicker
   * Updates the context state with new date and notifies parent
   */
  const handleDateChange = useCallback(
    newDate => {
      if (isUpdatingRef.current) return;

      isUpdatingRef.current = true;
      const currentDateTime = dates[fieldName];

      // Calculate new datetime (same logic as reducer)
      const currentTime = formatDate({
        date: currentDateTime,
        format: DATE_TIME_CONSTANTS.HH_mm,
      });

      const newDateTime = getISOString(
        getDateMomentObjectCustom(
          `${formatDate({
            date: newDate,
            format: DATE_TIME_CONSTANTS.YYYY_MM_DD,
          })}:${currentTime}`,
          DATE_TIME_CONSTANTS.YYYY_MM_DD__HH_mm
        )
      );

      // Update context state
      updateDate(fieldName, newDate);

      // Notify parent with the new datetime and type
      if (onChange) {
        onChange(fieldName, newDateTime, "date");
      }

      isUpdatingRef.current = false;
    },
    [fieldName, updateDate, onChange, dates]
  );

  /**
   * Handles time change from TimePicker (DS 2.0)
   * Converts moment object to HH:mm format before updating context state and notifies parent
   */
  const handleTimeChange = useCallback(
    newTime => {
      if (isUpdatingRef.current) return;

      // TimePicker returns a moment object, but context expects "HH:mm" format
      if (newTime && newTime.isValid && newTime.isValid()) {
        isUpdatingRef.current = true;
        const formattedTime = newTime.format("HH:mm");

        // Update context state
        updateTime(fieldName, formattedTime);

        // Notify parent with the new datetime and type
        if (onChange) {
          onChange(fieldName, formattedTime, "time");
        }

        isUpdatingRef.current = false;
      }
    },
    [fieldName, updateTime, onChange]
  );

  /**
   * Handles validation state changes from TimeSelectorV2
   * Updates validation state in context and notifies parent if callback provided
   */
  const handleValidationChange = useCallback(
    isInvalid => {
      if (isInvalid) {
        setValidationError(fieldName, true);
      } else {
        clearValidationError(fieldName);
      }

      // Notify parent component if callback provided
      if (onValidationChange) {
        onValidationChange(isInvalid);
      }
    },
    [fieldName, setValidationError, clearValidationError, onValidationChange]
  );

  return {
    // Current value from context
    dateTime: dates[fieldName],
    // Validation state from context
    isTimeInvalid: validationErrors[fieldName],
    // Action handlers
    handleDateChange,
    handleTimeChange,
    handleValidationChange,
  };
};

export default useDateTimeField;
